/**
 * ContextAccumulator — Collects workspace events between agent calls.
 *
 * Subscribes to the WorkspaceEventBus and accumulates a structured list
 * of what the user did since the last agent request. When the user sends
 * a chat message, Workspace drains this accumulator and includes the
 * deltas in the agent request so the AI sees every manual change.
 */

import type { WorkspaceEvent, WorkspaceEventBus } from './workspace-event-bus'

export interface ContextDelta {
  type: string
  summary: string
  timestamp: number
}

export class ContextAccumulator {
  private deltas: ContextDelta[] = []
  private unsubscribe: (() => void) | null = null

  /** Subscribe to a WorkspaceEventBus and start accumulating. */
  attach(bus: WorkspaceEventBus): void {
    this.unsubscribe = bus.on((event) => {
      this.deltas.push({
        type: event.type,
        summary: this.summarize(event),
        timestamp: Date.now(),
      })

      // Cap at 50 events to prevent unbounded growth
      if (this.deltas.length > 50) {
        this.deltas = this.deltas.slice(-50)
      }
    })
  }

  /** Detach from the event bus. */
  detach(): void {
    this.unsubscribe?.()
    this.unsubscribe = null
  }

  /** Drain all accumulated deltas (returns them and clears the buffer). */
  drain(): ContextDelta[] {
    const result = [...this.deltas]
    this.deltas = []
    return result
  }

  /** Check if there are any pending deltas. */
  get hasPending(): boolean {
    return this.deltas.length > 0
  }

  private summarize(event: WorkspaceEvent): string {
    switch (event.type) {
      case 'code_edit':
        return `Edited G-code in "${event.operationId}": ${event.summary} (${event.linesChanged} lines)`
      case 'material_change':
        return `Changed material from "${event.oldMaterial}" to "${event.newMaterial}"`
      case 'tool_added':
        return `Added tool T${event.toolNumber} (${event.toolType}, ${event.diameter}mm)`
      case 'tool_removed':
        return `Removed tool T${event.toolNumber} (${event.description})`
      case 'tool_modified':
        return `Modified tool T${event.toolNumber}: ${event.changes}`
      case 'work_offset_change':
        return `Set work offset ${event.register} to X=${event.x} Y=${event.y} Z=${event.z}`
      case 'operation_selected':
        return `Switched to operation "${event.label}"`
      case 'operation_added':
        return `Added new operation "${event.label}"`
      case 'operation_removed':
        return `Removed operation "${event.label}"`
      case 'stock_size_change':
        return `Changed stock size from ${event.oldSize} to ${event.newSize}`
      case 'units_change':
        return `Changed units from ${event.oldUnits} to ${event.newUnits}`
    }
  }
}
